import React from "react";
import AsyncSelect from "react-select/async";
import AsyncCreatableSelect from "react-select/async-creatable";
import { useLazyGetCustomersQuery, useLazySearchCustomersAllBranchesQuery } from "../../masters/customers/customersApi";

const toOption = (c) => ({
  value: c.custId,
  label: `${c.custName} (${c.branchCode || c.custId})`,
  customer: c,
});

// "Search Customer Master" picker used everywhere a Stock document needs a
// party (LPO/Stock-In/Stock-Return supplier, Stock-Out's customer recipient)
// - there's no separate Supplier master in this system, per direct
// instruction everything comes from Customer Master.
//
// mode="all-branches" (every real call site today) searches every branch -
// safe because every party field in the Stock module now carries a real
// paired *BranchId column (SupplierBranchId on LPO/Stock-In/Stock-Return as
// of Batch44_StockModuleAmendments.sql, IssuedToCustomerBranchId on
// Stock-Out from the start), so the API can always resolve Address/Mobile
// for print without guessing which branch the party belongs to.
//
// mode="branch" restricts the search to one `branchId` - kept as a general
// capability but unused by any current caller now that every document has
// a proper branch pairing.
//
// `creatable` (LPO/Stock-In/Stock-Return supplier only, never Stock-Out)
// lets the operator type a name that isn't in Customer Master at all and
// use it as-is. The resulting option has customer: null - callers must
// treat that as "no linked custId", not an error.
const PartyPicker = ({ mode = "all-branches", branchId, value, onChange, disabled, creatable = false }) => {
  const [triggerBranchSearch] = useLazyGetCustomersQuery();
  const [triggerAllBranchesSearch] = useLazySearchCustomersAllBranchesQuery();

  const loadOptions = async (inputValue) => {
    if (!inputValue || inputValue.trim().length === 0) return [];
    if (mode === "all-branches") {
      const { data = [] } = await triggerAllBranchesSearch(inputValue.trim());
      return data.map(toOption);
    }
    if (!branchId) return [];
    const { data = [] } = await triggerBranchSearch({ branchId, search: inputValue.trim() });
    return data.map(toOption);
  };

  const isReady = mode === "all-branches" || Boolean(branchId);
  const Select = creatable ? AsyncCreatableSelect : AsyncSelect;

  return (
    <Select
      classNamePrefix="react-select"
      cacheOptions
      loadOptions={loadOptions}
      value={value}
      onChange={(opt) =>
        onChange(opt && opt.__isNew__ ? { value: null, label: opt.label, customer: null } : opt)
      }
      isDisabled={disabled || !isReady}
      isClearable
      placeholder={isReady ? "Search Customer Master" : "Select a branch first"}
      formatCreateLabel={(input) => `Use "${input}" (not in Customer Master)`}
      noOptionsMessage={({ inputValue }) =>
        inputValue ? "No matching customers" : "Type to search Customer Master"
      }
    />
  );
};

export default PartyPicker;
